import React from "react";
import MUIDataTable from "mui-datatables";

import { withStyles, makeStyles } from "@material-ui/core/styles";

import FilterDropdown from "./FilterDropdown";
import RowHint from "./RowHint";
import { sortRows } from "./tableSort";
import { tableTextLabels } from "./tableTextLabels";
import { matchesSearch } from "./searchMatch";

// Подпись под названием культуры: в колонке остаток в тоннах, без подписи не понять, что за число.
export const LEFTOVER_UNIT_LABEL = "Остаток, т";

// Helper function to transform data
const transformData = (data, columns) => {
  const newData = data.map((row) => {
    let newRow = { ...row };
    const leftovers = row.leftovers || {};
    Object.keys(leftovers).forEach((key) => {
      newRow[`leftovers_${key}`] = leftovers[key];
    });
    return newRow;
  });

  const newColumns = columns.reduce((acc, col) => {
    if (col.name === "leftovers") {
      const leftoversKeys = Object.keys((data[0] && data[0].leftovers) || {});
      const newCols = leftoversKeys.map((key) => ({
        label: key,
        name: `leftovers_${key}`,
        options: {
          filter: false,
          customHeadLabelRender: () => (
            <div style={{ lineHeight: 1.25 }}>
              {key}
              <div style={{ fontSize: "11px", fontWeight: 400, opacity: 0.7 }}>
                {LEFTOVER_UNIT_LABEL}
              </div>
            </div>
          ),
          setCellProps: (value) => ({
            style: {
              textAlign: "center",
              minWidth: "120px"
            },
          }),
          customSortValue: (value) => {
            // Handle custom sorting logic
            if (value === null) {
              return Number.MAX_SAFE_INTEGER; // Place null values at the end
            } else if (typeof value === "number") {
              return value; // Directly return numeric values
            } else {
              return value; // Handle other types as needed
            }
          },
        },
      }));
      return [...acc, ...newCols];
    }
    return [...acc, col];
  }, []);

  return { newData, newColumns };
};

const text = (value) => (value === null || value === undefined ? "" : String(value));

// Как значение колонки видно на экране: колонка может писать его функцией («Да» / «Нет» вместо 1 / 0),
// и в списке фильтра человек должен видеть то же самое.
const shownValue = (column, value) => {
  const render = column.options && column.options.customBodyRender;
  if (typeof render === "function") {
    try {
      const shown = render(value);
      if (typeof shown === "string" || typeof shown === "number") return text(shown);
    } catch (error) {
      // функция ждёт другие аргументы: берём само значение
    }
  }
  return text(value);
};

// Каждый фильтр панели это выпадающий список с поиском и несколькими вариантами (а не столбик из
// галочек). Названия в списке берутся из строк таблицы: без повторов, без пустых, по алфавиту. Колонка
// без подписи (id и подобные) фильтра не получает.
const withDropdownFilters = (columns, data) =>
  columns.map((column) => {
    const options = column.options || {};
    if (options.filter === false || options.filterType || options.filterOptions) return column;
    if (!column.label) return { ...column, options: { ...options, filter: false } };

    const names = Array.from(new Set(data.map((row) => shownValue(column, row[column.name])).filter(Boolean))).sort((a, b) =>
      a.localeCompare(b, "uk")
    );
    return {
      ...column,
      options: {
        ...options,
        filterType: "custom",
        filterOptions: {
          fullWidth: true,
          // true значит «строку скрыть»: выбраны названия, а у строки не из них. Значение приходит уже так, как
          // оно видно на экране.
          logic: (value, selected) => selected.length > 0 && !selected.includes(text(value)),
          display: (filterList, onChange, index) => (
            <FilterDropdown
              label={column.label}
              options={names}
              value={filterList[index] || []}
              // «Мультивыбор» говорит библиотеке хранить список названий как есть (иначе она кладёт его ещё в
              // один массив), и тогда над таблицей у каждого названия своя метка, которая снимается отдельно.
              onChange={(selected) => onChange(selected, index, { name: column.name, filterType: "multiselect" })}
            />
          ),
        },
        // Метка над таблицей: «Область: Одеська», по метке на каждое выбранное название.
        customFilterListOptions: { render: (names) => [].concat(names).map((name) => `${column.label}: ${name}`) },
      },
    };
  });

const useStyles = makeStyles({
  // The panel of the filter is drawn in a popover outside the table, and the library puts the cross
  // that closes it (48px wide, 4px from the edge) right over the "RESET" button. The button is
  // moved left of the cross, so that a click on it cannot close the panel instead.
  '@global': {
    'button[data-testid="filterReset-button"]': {
      marginRight: "56px",
      marginLeft: "24px",
      padding: "4px 8px",
      minWidth: 0,
      fontSize: "13px",
      lineHeight: 1.43,
    },
    // «ФИЛЬТРЫ» и «СБРОСИТЬ» на одной линии: библиотека ставит текст и кнопку рядом как строчные
    // блоки, и кнопка со своими отступами сидела ниже слова.
    'div:has(> button[data-testid="filterReset-button"])': {
      display: "flex",
      alignItems: "center",
    },
    // Фильтры это выпадающие списки на всю ширину панели: у панели есть своя ширина, а не по самому
    // длинному из них.
    '.MuiPopover-paper:has(button[data-testid="filterReset-button"])': {
      minWidth: "min(440px, 94vw)",
      maxWidth: "min(560px, 96vw)",
    },
  },
  tableCell: {
    minWidth: "100px",
    "& .MUIDataTableBodyCell-root-90": {
      minWidth: "100px",
    }
  }
})

function SearchedTable(props) {
  const { columns, data } = props.tableDescription;
  const { newData, newColumns } = transformData(data, columns);

  const styles = useStyles()
  const hintRef = React.useRef(null)

  const options = {
    filterType: "checkbox",
    selectableRows: "none",
    onRowClick: props.clickAction,
    sortOrder: props.sortOrder,
    rowHover: true,
    pagination: true,
    setRowProps: (row, dataIndex, rowIndex) => {
      const rowProps = { style: { backgroundColor: row[1] ? "" : "antiquewhite" } };
      // rowHint(row) — текст подсказки для строки (например, почему она бежевая) или пусто.
      const text = props.rowHint && props.rowHint(row);
      if (text) {
        rowProps.onMouseEnter = (event) => hintRef.current && hintRef.current.show(text, event.clientX, event.clientY);
        rowProps.onMouseLeave = () => hintRef.current && hintRef.current.hide();
      }
      return rowProps;
    },
    rowsPerPageOptions: [100, 1000, 5000],
    rowsPerPage: 100,
    customSort: sortRows,
    // Поиск над таблицей: текст без учёта регистра, номер по цифрам («+380 50 457» найдёт «+380504570272»).
    customSearch: (query, row, cols) => {
      // Как и обычный поиск таблицы: только видимые колонки, без скрытых id и служебных.
      const cells = row.filter((cell, index) => cols[index] && cols[index].display === "true" && cols[index].searchable !== false);
      return matchesSearch(query, cells, cells);
    },
    textLabels: tableTextLabels,
  };

  return (
    <>
      <MUIDataTable
        title={props.tableTitle ? props.tableTitle : "Без названия"}
        data={newData}
        className={styles.tableCell}
        columns={withDropdownFilters(newColumns, newData)}
        options={options}
      />
      {props.rowHint && <RowHint ref={hintRef} />}
    </>
  );
}

export default SearchedTable;
